import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Container,
  Chip,
  Stack,
  Button,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { Article, CloudDownload, Print, Fullscreen, FullscreenExit } from '@mui/icons-material';

function FastDocumentViewer({ documentUrl, title, onLoadComplete }) {
  const [loading, setLoading] = useState(true);
  const [isFullSize, setIsFullSize] = useState(false);

  useEffect(() => {
    // Reset loading state when documentUrl changes
    setLoading(true);
  }, [documentUrl]);

  const handleDownload = async () => {
    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = title || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback to direct download
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = title || 'document.pdf';
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open(documentUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const toggleFullSize = () => {
    setIsFullSize(!isFullSize);
    if (!isFullSize) {
      // Scroll to top when entering full size mode
      window.scrollTo(0, 0);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        ...(isFullSize && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          height: '100vh'
        })
      }}
    >
      {/* Enhanced Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
          color: 'white',
          borderRadius: 0,
          flexShrink: 0
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ px: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Article sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {title || 'Document'}
              </Typography>
              <Chip
                label="Technical Documentation"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.7rem'
                }}
              />
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Tooltip title={isFullSize ? "Exit Full Size" : "View Full Size"}>
              <Button
                variant="contained"
                startIcon={isFullSize ? <FullscreenExit /> : <Fullscreen />}
                onClick={toggleFullSize}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {isFullSize ? 'Exit Full Size' : 'View Full Size'}
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<CloudDownload />}
              onClick={handleDownload}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Download
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Print
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Document Viewer */}
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          minHeight: isFullSize ? 'calc(100vh - 100px)' : '1500px',
          height: isFullSize ? 'calc(100vh - 100px)' : 'auto'
        }}
      >
        <iframe
          src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width`}
          width="100%"
          style={{
            border: 'none',
            height: isFullSize ? 'calc(100vh - 100px)' : '1500px'
          }}
          title={title}
          onLoad={() => {
            setLoading(false);
            if (onLoadComplete) {
              onLoadComplete();
            }
          }}
        />
      </Box>
    </Box>
  );
}

export default FastDocumentViewer;
